import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { taskPaths } from '../../tasks/queries/paths';
import { taskKeys } from '../../tasks/queries/keys';
import { tierKeys } from '../../tiers/queries/keys';
import { tierPaths } from '../../tiers/queries/paths';
import { buildPlanMap } from '../utils/map';
import { usePlan } from './plans';

// TSS vite/03 — server state for the map of a plan.
//
// TWO READS FOR THE TRUNK, and no more: the plan with its versions, and the
// tiers OF ITS PROJECT. `GET /admin/tiers` filters by project, phase and state
// — not by plan version — so the grouping happens in utils/map.js. A hundred
// tiers is the cap and far more than a project has.
//
// THE TASKS ARE NOT PART OF IT. They are read per tier, and only when somebody
// opens that tier: a plan with twenty tiers would otherwise fire twenty
// requests to draw a tree most of which nobody unfolds.

const TIERS_LIMIT = 100;
const TASKS_LIMIT = 100;

export function usePlanMap(planId) {
    const plan = usePlan(planId);
    const projectId = plan.data?.project_id ?? plan.data?.plan?.project_id ?? null;

    const params = { limit: TIERS_LIMIT, offset: 0, project_id: projectId };
    const tiers = useQuery({
        queryKey: tierKeys.list(params),
        queryFn: ({ signal }) => api.get(tierPaths.list(), { params, signal }).then(r => r.data),
        enabled: !!projectId,
    });

    const map = useMemo(
        () => buildPlanMap(plan.data, tiers.data?.items ?? []),
        [plan.data, tiers.data],
    );

    return {
        map,
        isLoading: plan.isLoading || (!!projectId && tiers.isLoading),
        error: plan.error ?? tiers.error ?? null,
        // Said on screen: a project with more tiers than the cap would draw an
        // incomplete tree, and an incomplete tree looks exactly like a complete one.
        truncated: (tiers.data?.pagination?.total ?? 0) > TIERS_LIMIT,
    };
}

// The tasks of one tier, asked for when its node opens. Same key as everywhere
// else, so authorizing or accepting one refreshes this too.
export function useTierTasks(tierId, { enabled = false } = {}) {
    const params = { limit: TASKS_LIMIT, offset: 0, tier_id: tierId };

    return useQuery({
        queryKey: taskKeys.list(params),
        queryFn: ({ signal }) => api.get(taskPaths.list(), { params, signal }).then(r => r.data),
        enabled: enabled && !!tierId,
        staleTime: 30_000,
    });
}
